import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useGroups } from "@/lib/hooks/usePeople";
import { COMPOSER_URL } from "@/lib/links";
import { collator as collatorFor } from "@/i18n";

/**
 * What a teacher sees on Backstage's front page, once the welcome is dismissed.
 *
 * Before this there was nothing: a heading, "Hello, <name>.", and an empty
 * field forever — found in the live run on 2026-09-09, and the first thing a
 * teacher meets every morning after their first day. The panel that greeted
 * them is a one-off; this is the page underneath it.
 *
 * Three things, in the order a teacher needs them: the classes they teach,
 * where the lesson is actually run, and the other app. Everything here is a
 * link to a place that already exists — no new callable, no new collection.
 */
export function TeacherHome() {
  const t = useT();
  const { locale } = useI18n();
  const { user } = useAuth();
  const { groups, loading } = useGroups();

  const mine = groups
    .filter((group) => !group.archivedAt)
    .filter((group) => (user ? group.teacherUids.includes(user.uid) : false))
    .sort((a, b) => collatorFor(locale).compare(a.name, b.name));

  /*
    A teacher with no class of their own is not an error and not an empty
    state: somebody has simply not put them on one yet, and the honest thing
    is to say so and point at the list of all of them.
  */
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <section aria-labelledby="teacher-home-groups" className="card p-6">
        <h2 id="teacher-home-groups" className="section-title">
          {t("home.teacher.groups")}
        </h2>

        {loading ? (
          <p className="mt-2 text-sm text-ink-muted">{t("common.loading")}</p>
        ) : mine.length === 0 ? (
          <>
            <p className="mt-2 text-sm text-ink-muted">{t("home.teacher.noGroups")}</p>
            <Link to="/groups" className="mt-4 inline-block text-sm font-medium text-brand underline underline-offset-2 hover:text-brand-hover">
              {t("home.teacher.allGroups")}
            </Link>
          </>
        ) : (
          <>
            <ul className="mt-4 flex flex-col gap-2">
              {mine.slice(0, 6).map((group) => (
                <li key={group.id} className="text-sm">
                  <Link to={`/groups/${group.id}`} className="font-medium text-ink hover:underline">
                    {group.name}
                  </Link>
                  <span className="ml-2 text-ink-muted">
                    {t("home.teacher.students", { count: group.roster.length })}
                  </span>
                </li>
              ))}
            </ul>
            <Link to="/groups" className="mt-4 inline-block text-sm font-medium text-brand underline underline-offset-2 hover:text-brand-hover">
              {t("home.teacher.allGroups")}
            </Link>
          </>
        )}
      </section>

      <section aria-labelledby="teacher-home-next" className="card p-6">
        <h2 id="teacher-home-next" className="section-title">
          {t("home.teacher.next")}
        </h2>
        <ul className="mt-4 flex flex-col gap-4 text-sm">
          <li>
            <a href={COMPOSER_URL} className="font-medium text-ink hover:underline">
              {t("nav.openComposer")}
              <span aria-hidden="true" className="ml-1 text-ink-faint">↗</span>
            </a>
            <p className="mt-0.5 text-ink-muted">{t("home.teacher.composerBody")}</p>
          </li>
          <li>
            <Link to="/sessions" className="font-medium text-ink hover:underline">
              {t("home.teacher.lessons")}
            </Link>
            <p className="mt-0.5 text-ink-muted">{t("home.teacher.lessonsBody")}</p>
          </li>
          <li>
            <Link to="/kits" className="font-medium text-ink hover:underline">
              {t("home.teacher.kits")}
            </Link>
            <p className="mt-0.5 text-ink-muted">{t("home.teacher.kitsBody")}</p>
          </li>
        </ul>
      </section>
    </div>
  );
}
